import { useCallback, useEffect, useState, useRef } from 'react';

// Define the SpeechRecognition interface with the stop method
interface SpeechRecognitionWithStop extends SpeechRecognition {
  stop: () => void;
}

type UseSpeechToTextReturn = {
  isListening: boolean;
  startListening: () => void;
  stopListening: () => void;
  isSpeechSupported: boolean;
};

const useSpeechToText = (
  onAsk: (text: string, type: 'voice' | 'text' | 'recommended') => void
): UseSpeechToTextReturn => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionWithStop | null>(null);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSpeechSupported(!!SpeechRecognition);

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition() as SpeechRecognitionWithStop;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results?.[0]?.[0]?.transcript;
        if (transcript) {
          onAsk(transcript, 'voice');
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onAsk]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
      }
    }
  }, [isListening]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }
  }, [isListening]);

  return {
    isListening,
    startListening,
    stopListening,
    isSpeechSupported,
  };
};

export default useSpeechToText;
